/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { SyntheticEvent, useState } from 'react';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import 'rc-tooltip/assets/bootstrap.css';
import { Clipboard, ClipboardCheck } from 'lucide-react';

interface Props {
  className?: string;
  href: string;
  title: string;
  formatter?: (link: string) => Promise<string>;
}

export const Link: React.FC<Props> = ({ className, href, title, formatter }) => {
  const { t } = useTranslation();

  const [copied, setCopied] = useState(false);

  const onCopy = async (ev: SyntheticEvent) => {
    setCopied(true);
    const el = ev.currentTarget as HTMLAnchorElement;
    const text = formatter ? await formatter(href) : href;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        el.classList.add('bg-green-500');
        setTimeout(() => {
          el.classList.remove('bg-green-500');
          setCopied(false);
        }, 1000);
      })
      .catch(() => {
        setCopied(false);
      });
  };
  return (
    <div className="flex  align-items-center justify-content-between py-0">
      <div className="flex align-items-center">
        <a
          style={{ all: 'unset' }}
          id={title}
          href={href}
          target={'_blank'}
          className={classNames(className, 'cursor-pointer')}
        >
          <span className="inline-block font-semibold">{title}</span>
        </a>
        {copied ? (
          <ClipboardCheck className="mx-2 cursor text-green-500" size={18} />
        ) : (
          <Clipboard onClick={onCopy} className="mx-2 cursor-pointer" size={18} />
        )}
      </div>

      <div className="flex align-items-end">
        <Button
          onClick={async () => {
            const link = formatter ? await formatter(href) : href;
            window.open(link, '__blank');
          }}
          size="small"
          icon="pi pi-external-link"
          label={t(['actions.open', 'Open'])}
        />
      </div>
    </div>
  );
};
